import { useCallback, useMemo, useState } from "react";
import { Device } from "../Types";

const PINNED_DEVICES_KEY = "pinnedDevices";
const MAX_PINNED_DEVICES = 3;

export const usePinnedDevices = () => {
  const [pinnedDevices, setPinnedDevices] = useState<Device[]>(() => {
    try {
      const stored = localStorage.getItem(PINNED_DEVICES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      return [];
    }
  });

  const pinnedDeviceIds = useMemo(() => {
    return new Set(pinnedDevices.map((device: Device) => device.id));
  }, [pinnedDevices]);

  const isPinned = useCallback(
    (deviceId: string): boolean => {
      return pinnedDeviceIds.has(deviceId);
    },
    [pinnedDeviceIds],
  );

  const pinDevice = useCallback(
    (device: Device): { success: boolean; error?: string } => {
      if (pinnedDeviceIds.has(device.id)) {
        return { success: true };
      }

      if (pinnedDevices.length >= MAX_PINNED_DEVICES) {
        return {
          success: false,
          error: `Maximum ${MAX_PINNED_DEVICES} devices can be pinned`,
        };
      }

      const updatedPinned = [...pinnedDevices, device];

      try {
        localStorage.setItem(PINNED_DEVICES_KEY, JSON.stringify(updatedPinned));
        setPinnedDevices(updatedPinned);
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: "Failed to save pinned device",
        };
      }
    },
    [pinnedDevices, pinnedDeviceIds, setPinnedDevices],
  );

  const unpinDevice = useCallback(
    (deviceId: string): void => {
      const updatedPinned = pinnedDevices.filter(
        (device: Device) => device.id !== deviceId,
      );

      localStorage.setItem(PINNED_DEVICES_KEY, JSON.stringify(updatedPinned));
      setPinnedDevices(updatedPinned);
    },
    [pinnedDevices, setPinnedDevices],
  );

  const updatePinnedDevice = useCallback(
    (freshDevice: Device): void => {
      const deviceIndex = pinnedDevices.findIndex(
        (device: Device) => device.id === freshDevice.id,
      );

      if (deviceIndex !== -1) {
        const updatedPinned = [...pinnedDevices];
        updatedPinned[deviceIndex] = freshDevice;

        localStorage.setItem(PINNED_DEVICES_KEY, JSON.stringify(updatedPinned));
        setPinnedDevices(updatedPinned);
      }
    },
    [pinnedDevices, setPinnedDevices],
  );

  const getPinnedDeviceIds = useCallback((): string[] => {
    return pinnedDevices.map((device: Device) => device.id);
  }, [pinnedDevices]);

  return {
    pinnedDevices,
    pinnedDeviceIds,
    isPinned,
    pinDevice,
    unpinDevice,
    updatePinnedDevice,
    getPinnedDeviceIds,
  };
};
